<template>
    <div>
        <div class="card">
            <div class="bg-blue-light p-1">
                <TitleButton
                    title="Ongoing Service"
                    :showAddNew="false"
                />
            </div>

            <div class="my-2 px-2">
                <DateQuerySetter
                    btnTitle="Go"
                    @onClickGo="getOPDBillings"
                    :isHideDate="true"
                >
                    <div class="col-12 col-sm-6 col-md-5 col-lg-4">
                        <label for="colFormLabel" class="col-form-label">Patient</label>
                        <AsyncSelect
                            placeholder="Patient Name, ID, Mobile No"
                            v-model="patient"
                            :api-service="fetchContactProfiles"
                            :additional-query="additionalQuery"
                            :format-label="formatPatientLabel"
                        />
                    </div>
                    <div v-if="contactId" class="col-6 col-sm-3 col-md-3 col-lg-3">
                        <label for="colFormLabel" class="col-form-label">Service No</label>
                        <AsyncSelect
                            :key="reRenderKey"
                            placeholder="Service No"
                            v-model="admissionNo"
                            :api-service="fetchIpdRegNumbers"
                            :additional-query="{ contact_profile_id: contactId, types: 'DC,DS', registration_status: 'released' }"
                            :format-label="formatPatientLabel"
                        />
                    </div>
                    <div v-show="contactId" class="col-12 col-sm-6 col-md-4 col-lg-4">
                        <label class="col-form-label col-xl-3">From</label>
                        <input
                            v-model="startDate"
                            type="date"
                            class="form-control date-mask" 
                            placeholder="From"
                        >
                    </div>
                    
                    <div v-show="contactId" class="col-12 col-sm-6 col-md-4 col-lg-4">
                        <label class="col-form-label 'col-xl-3'">To</label>
                        <input
                            v-model="endDate"
                            type="date"
                            class="form-control date-mask" 
                            placeholder="From"
                        >   
                    </div>
                </DateQuerySetter>
            </div>

            <div class="col-12 px-2 my-2">
                <ListTable :serviceList="serviceList" :company="company"/>
            </div>

            <div class="px-2 position-absolute bottom-0">
                <Pagination
                    :offset="offset"
                    @onPageChange="onPageChange"
                    ref="pagination"
                />
            </div>
        </div>

        <AddAppointmentModal v-if="$store.state.isModalOpen"/>
        <Loader v-if="loading"/>
    </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton.vue';
import DateQuerySetter from '@/components/atom/DateQuerySetter.vue';
import ListTable from '@/components/molecule/company/dental-os/appointment/TreatmentTable.vue';
import AddAppointmentModal from '@/components/molecule/company/dental-os/appointment/AddAppointmentModal.vue';
import {useRoute, useRouter} from "vue-router";
import handleDentalOs from "@/services/modules/dental-os/dentalOs";
import {computed, inject, onMounted, ref, watch} from "vue";
import Loader from "@/components/atom/LoaderComponent.vue";
import Pagination from "@/components/atom/Pagination.vue";
import {useStore} from "vuex";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import handleContact from "@/services/modules/contact";
import {useAsyncDropdownHelper} from "@/services/utils/asyncDropdownHelper";
import handleHospital from '@/services/modules/hospital'

const $route = useRoute();
const $router = useRouter();
const $store = useStore();

const showError = inject('showError');
const {fetchIpdRegNumbers} = handleHospital();
const {fetchServiceList} = handleDentalOs();
const {fetchContactProfiles} = handleContact()
const {formatPatientLabel} = useAsyncDropdownHelper()

const offset = ref(10);
const loading = ref(false);
const serviceList = ref([])
const patient = ref(null)
const company = ref({})
const page = computed(() => $route.query.page);
const companyId = computed(() => $route.params.companyId);
const reRenderKey = ref(0);
const admissionNo = ref(null);
const contactId = computed(() => patient.value ? patient.value.id : '');
const startDate = ref(null);
const endDate = ref(null);

const additionalQuery = {
    type: "patient"
}

const setPaginationData = (data) => {
    $store.commit('setPaginationData', data)
};

const resetPagination = () => {
    $store.commit('resetPagination')
};

const onPageChange = async (page) => {
    let routeQuery = Object.assign({}, $route.query)
    routeQuery.page = page
    await $router.push({path: $route.path, query: routeQuery})
    await getOPDBillings()
}

const setPagination = (data) => {
    setPaginationData({
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    })
}

const getQuery = () => {
    let query = '?company_id=' + companyId.value
    query += '&offset=' + offset.value
    query += '&service_status=completed'
    if (startDate.value) query += '&start_date=' + startDate.value
    if (endDate.value) query += '&end_date=' + endDate.value
    if (page.value) query += '&page=' + page.value
    if (patient.value) query += '&contact_profile_id=' + patient.value.id
    if (admissionNo.value) query += '&ipd_register_id=' + admissionNo.value.id;
    return query
};

async function getOPDBillings() {

    if (checkValidation()) {
        return;
    }

    loading.value = true;
    try {
        const res = await fetchServiceList(getQuery());
        if (!res.status) {
            serviceList.value = []
            return $store.commit("resetPagination")
        }
        serviceList.value = res.data.data;
        if (serviceList.value.length > 0) {
            setPagination(res.data)
        }
    } catch (err) {
        if (err.response) {
            showError(err.response.message)
        }
    } finally {
        loading.value = false;
    }
}

const checkValidation = () => {
    let flag = false;

    if (!patient.value) {
        showError('Patient is required');
        flag = true;
    }

    if ( ! admissionNo.value) {
        showError('Service No is required');
        flag = true;
    }

    return flag;
}

watch(contactId, (newVal, oldVal) => {
    reRenderKey.value = newVal;
    admissionNo.value = null;
})

</script>